import React, { useCallback, useEffect, useState } from 'react';
import { Check, Flag, History, KeyRound, Loader2, Pencil, TrendingUp } from 'lucide-react';
import { apiService } from '../../../services/api';
import { euros, hours } from '../ui';
import { ChangeLine } from './ChangesHistory';
import { changeWhen, shortDay } from './changes';

// Pilotage d'un projet : itération en cours, temps et achats consommés face au budgété, % de réalisation de l'itération
// (fixé par le titulaire de la roadmap, il se compare à son budget temps), prochain jalon, derniers changements tracés.
// Données de GET /projects/{id}/steering.

const pctOf = (value) => (value == null ? null : Math.round(value));

const Meter = ({ value, marker = null, tone }) => (
  <div className="relative h-2 overflow-hidden rounded-full bg-slate-100">
    <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, Math.max(0, value || 0))}%` }} />
    {marker != null && marker > 0 && marker < 100 && (
      <span className="absolute inset-y-0 w-0.5 bg-slate-500/70" style={{ left: `${marker}%` }} title="Attendu à date" />
    )}
  </div>
);

const toneFor = (consumedPct, expectedPct) => {
  if (consumedPct == null) return 'bg-slate-300';
  if (consumedPct > 100) return 'bg-rose-500';
  if (expectedPct != null && consumedPct > expectedPct + 10) return 'bg-amber-400';
  return 'bg-emerald-500';
};

/** Une ligne de budget : « Temps 152 h / 320 h », barre, et l'attendu à date pour les heures. */
export const BudgetLine = ({ label, consumed, budget, format, expected = null, note = null, empty = 'pas de budget', title }) => {
  const consumedPct = budget > 0 ? (consumed / budget) * 100 : null;
  const expectedPct = budget > 0 && expected != null ? (expected / budget) * 100 : null;
  return (
    <div title={title}>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-800">
          <span className={`font-semibold ${consumedPct > 100 ? 'text-rose-600' : ''}`}>{format(consumed)}</span>
          <span className="text-slate-400"> / {budget > 0 ? format(budget) : empty}</span>
          {consumedPct != null && <span className="ml-1.5 text-slate-500">{pctOf(consumedPct)} %</span>}
        </span>
      </div>
      <Meter value={consumedPct} marker={expectedPct} tone={toneFor(consumedPct, expectedPct)} />
      {note && <p className="mt-1 text-[11px] text-slate-400">{note}</p>}
    </div>
  );
};

/** % de réalisation d'une itération : lu par tous, fixé par le titulaire de la roadmap (ou par tous s'il n'y en a pas). */
export const ProgressEditor = ({ progress, roadmap, onSave, label = 'Réalisation de l’itération' }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const percent = progress?.percent;
  const holders = roadmap?.holders || [];
  const can = roadmap ? roadmap.can !== false : true;
  const open = () => { setValue(percent != null ? String(Math.round(percent)) : ''); setComment(''); setEditing(true); };
  const save = async (event) => {
    event.preventDefault();
    const number = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(number) || number < 0 || number > 100) return;
    setSaving(true);
    try {
      await onSave(number, comment.trim());
      setEditing(false);
    } catch {
      // erreur affichée par l'appelant
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <span className="flex items-center gap-1.5">
          <span className="text-lg font-bold tabular-nums text-slate-900">{percent != null ? `${Math.round(percent)} %` : '—'}</span>
          {can && !editing && onSave && (
            <button type="button" onClick={open} title="Mettre à jour le % de réalisation"
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil size={12} /></button>
          )}
        </span>
      </div>
      <Meter value={percent} tone="bg-indigo-500" />
      {editing ? (
        <form onSubmit={save} className="mt-2 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <input type="number" min="0" max="100" step="5" value={value} onChange={(e) => setValue(e.target.value)} autoFocus
              aria-label="% de réalisation" className="w-20 rounded-md border border-slate-200 px-2 py-1 text-sm tabular-nums" />
            <span className="text-sm text-slate-500">%</span>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ce qui le justifie (facultatif)"
              className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs" />
          </div>
          <div className="flex justify-end gap-1">
            <button type="button" onClick={() => setEditing(false)} className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Annuler</button>
            <button type="submit" disabled={saving || value === ''}
              className="flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Enregistrer
            </button>
          </div>
        </form>
      ) : (
        <p className="mt-1 text-[11px] text-slate-400">
          {progress?.previous && (
            <span className="mr-1.5 inline-flex items-baseline gap-1 text-slate-500">
              <span>{Math.round(progress.previous.percent)} % le {shortDay(progress.previous.created_at)}</span>
              <span aria-hidden="true">→</span>
              <span className="font-semibold text-slate-700">{Math.round(percent)} %</span>
              <span className={`font-semibold ${percent - progress.previous.percent > 0 ? 'text-emerald-600' : percent < progress.previous.percent ? 'text-rose-600' : 'text-slate-400'}`}>
                ({percent - progress.previous.percent >= 0 ? '+' : '−'}{Math.abs(Math.round(percent - progress.previous.percent))} pts)
              </span>
              <span>·</span>
            </span>
          )}
          {progress
            ? <>Fixé par {progress.employee_name || 'quelqu’un'} le {changeWhen(progress.created_at)}{progress.comment ? <> : « {progress.comment} »</> : ''}</>
            : 'Pas encore estimé.'}
          {!can && holders.length > 0 && (
            <span className="ml-1 inline-flex items-center gap-1 text-violet-700">
              <KeyRound size={10} /> fixé par {holders.map((h) => h.name).join(', ')}
            </span>
          )}
        </p>
      )}
    </div>
  );
};

/** Heures à la fin de l'itération au rythme actuel : consommé ÷ % réalisé (100 h pour 80 % réalisés → 125 h). */
export const projectHours = (consumed, percent) =>
  (Number(percent) > 0 && Number(consumed) > 0 ? Number(consumed) / (Number(percent) / 100) : null);

/** Projection du temps de l'itération d'après son % de réalisation, face à son budget temps. */
export const HoursProjection = ({ consumed, budget, percent }) => {
  if (!(Number(consumed) > 0)) return null;
  const projected = projectHours(consumed, percent);
  if (projected == null) {
    return <p className="text-[11px] text-slate-400">Projection du temps : fixer le % de réalisation de l’itération.</p>;
  }
  const over = budget > 0 ? projected - budget : null;
  const overPct = budget > 0 ? (over / budget) * 100 : null;
  const tone = over == null ? 'text-slate-600' : overPct > 10 ? 'text-rose-600' : overPct > 0 ? 'text-amber-700' : 'text-emerald-700';
  return (
    <p className={`flex flex-wrap items-baseline gap-x-1.5 text-xs ${tone}`}>
      <TrendingUp size={12} className="translate-y-0.5 shrink-0" />
      <span>Projection à la fin : <span className="font-semibold tabular-nums">{hours(projected)}</span></span>
      {over != null && (
        <span className="tabular-nums">
          {over > 0
            ? `soit ${hours(over)} de plus que les ${hours(budget)} budgétées (+${Math.round(overPct)} %)`
            : `dans le budget de ${hours(budget)} (${hours(-over)} de marge)`}
        </span>
      )}
      <span className="w-full text-[10px] font-normal text-slate-400">
        {hours(consumed)} passées pour {Math.round(Number(percent))} % réalisés, au même rythme jusqu’au bout.
      </span>
    </p>
  );
};

const STATE_LABEL = { current: 'en cours', upcoming: 'à venir', done: 'terminée' };

/** Bloc complet pour la revue des projets de la tactique : charge et rafraîchit lui-même. */
export const ProjectSteering = ({ projectId, onOpenHistory = null }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setData(await apiService.get(`/projects/${projectId}/steering`));
      setError(null);
    } catch (err) {
      setError(err?.message || 'Pilotage indisponible');
    }
  }, [projectId]);
  useEffect(() => {
    setData(null);
    load();
    const timer = setInterval(load, 30000); // le % fixé en réunion par le titulaire apparaît chez tous
    return () => clearInterval(timer);
  }, [load]);

  if (error && !data) return <p className="text-sm text-rose-500">{error}</p>;
  if (!data) return <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement du pilotage…</p>;

  const saveProgress = (iterationId) => async (percent, comment) => {
    await apiService.put(`/projects/${projectId}/iterations/${iterationId}/progress`, { percent, comment: comment || null });
    await load();
  };
  const { current, project } = data;
  const main = current[0];
  const holders = data.domains?.roadmap?.holders || [];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
          {main ? (
            <>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Itération {STATE_LABEL[main.state] || ''}
                </span>
                <span className="text-sm font-semibold text-slate-900">{main.name}</span>
                <span className="text-xs text-slate-500">{shortDay(main.start_date)} → {shortDay(main.end_date)}</span>
                {main.state === 'current' && main.elapsed_pct != null && (
                  <span className="ml-auto text-xs tabular-nums text-slate-500">{Math.round(main.elapsed_pct)} % du temps écoulé</span>
                )}
              </div>
              <ProgressEditor progress={main.progress} roadmap={data.domains?.roadmap} onSave={saveProgress(main.id)} />
              <div className="space-y-1.5">
                <BudgetLine label="Temps" consumed={main.hours.consumed} budget={main.hours.budget} format={hours}
                  expected={main.state === 'current' ? main.hours.budget_to_date : null}
                  note={main.state === 'current' && main.hours.budget > 0
                    ? `Attendu à date : ${hours(main.hours.budget_to_date)}${main.hours.budget_source === 'estime_ca' ? ' (budget estimé d’après le CA)' : ''}`
                    : null} />
                <HoursProjection consumed={main.hours.consumed} budget={main.hours.budget} percent={main.progress?.percent} />
              </div>
              <BudgetLine label="Achats" consumed={main.purchases.consumed} budget={main.purchases.budget} format={euros} />
              {current.length > 1 && (
                <p className="text-[11px] text-slate-400">
                  En parallèle : {current.slice(1).map((it) => `${it.name} (${hours(it.hours.consumed)} / ${hours(it.hours.budget)}${
                    it.progress ? `, réalisée à ${Math.round(it.progress.percent)} %` : ''})`).join(' · ')}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-400">Pas d’itération datée sur ce projet.</p>
          )}
        </div>
        <div className="space-y-3 rounded-xl border border-slate-200 p-3.5">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-slate-500">Projet : temps</p>
              <p className={`font-semibold tabular-nums ${project.hours.consumed_pct > 100 ? 'text-rose-600' : 'text-slate-800'}`}>
                {hours(project.hours.consumed)} <span className="font-normal text-slate-400">/ {hours(project.hours.budget)}</span>
              </p>
            </div>
            <div>
              <p className="text-slate-500">Projet : achats</p>
              <p className={`font-semibold tabular-nums ${project.purchases.consumed_pct > 100 ? 'text-rose-600' : 'text-slate-800'}`}>
                {euros(project.purchases.consumed)} <span className="font-normal text-slate-400">/ {euros(project.purchases.budget)}</span>
              </p>
            </div>
          </div>
          {data.next_milestone && (
            <p className="flex items-center gap-1.5 border-t border-slate-100 pt-3 text-xs text-slate-600">
              <Flag size={12} className="text-rose-500" /> Prochain jalon : <span className="font-medium">{data.next_milestone.name}</span>
              <span className="text-slate-400">le {shortDay(data.next_milestone.date)}</span>
            </p>
          )}
        </div>
      </div>
      {(data.changes?.length > 0 || holders.length > 0) && (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              <History size={11} /> Derniers changements de roadmap et de budget
            </p>
            {holders.length > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-violet-700">
                <KeyRound size={11} /> {holders.map((h) => `${h.name} (${h.role})`).join(', ')}
              </span>
            )}
          </div>
          {data.changes?.length ? (
            <ul className="divide-y divide-slate-100">
              {data.changes.map((change) => <ChangeLine key={change.id} change={change} compact />)}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">Aucun changement depuis que la roadmap est tenue.</p>
          )}
          {onOpenHistory && (
            <button type="button" onClick={onOpenHistory} className="mt-1 text-[11px] font-semibold text-slate-500 hover:text-indigo-700">
              Tout l’historique
            </button>
          )}
        </div>
      )}
    </div>
  );
};
